import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { ScreenName as ScreenNameType } from '@helpcenter/constants';
import { ScreenName } from '@helpcenter/constants';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { UI_MODIFICATIONS_JSM_REQUEST_CREATE_RESOURCE } from '@helpcenter/portal-home-resource';
import * as grid from '@helpcenter/styles/src/grid';
import { withAsync } from '@helpcenter/with-async';
import { lazyForPaint } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import type { Route } from 'react-resource-router';
import { useRouter, withRouter } from 'react-resource-router';
import { ContextSetter } from 'view/common/context';
import { ScreenErrorBoundary } from 'view/layout/screen-error-boundary';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AnalyticsScreen } from '@atlassian/help-center-common-util/analytics/analytics-screen';

export const withEmbeddedPage = <TProps extends object>(
    WrappedComponent: React.ComponentType<TProps>,
    screenName: ScreenNameType
) => {
    const ComponentWithEmbeddedPage = (props: TProps) => {
        di(AnalyticsScreen, ScreenErrorBoundary, ContextSetter);
        const [routerState] = useRouter();
        const { basePath, ...routeContext } = routerState;

        return (
            <AnalyticsScreen screenName={screenName} screenId={routeContext.match.url}>
                <ScreenErrorBoundary screenName={screenName}>
                    <EmbedComponentWrapper>
                        <ContextSetter {...routeContext} />
                        <WrappedComponent isEmbeddedRoute {...routeContext} {...props} />
                    </EmbedComponentWrapper>
                </ScreenErrorBoundary>
            </AnalyticsScreen>
        );
    };

    return ComponentWithEmbeddedPage;
};

// Route for embeddable request create page
const AsyncEmbedCreateRequestPage = withAsync({
    loader: lazyForPaint(
        () =>
            import(
                /* webpackChunkName: "async-embed-request-create-page" */ '../../view/request-create-page/embed-request-create-page'
            )
    ),
});

export const getEmbedCreateRequestRoute = () => ({
    name: 'embed-request-create',
    path: `/embeds/portal/:portalId/create/:requestTypeId`,
    exact: true,
    component: withRouter(withEmbeddedPage(AsyncEmbedCreateRequestPage, ScreenName.EMBED_REQUEST_CREATE)),
    resources:
        isUiModificationsSupportEnabled() && !FeatureGates.checkGate('ditto_uim_root_query_rest')
            ? [UI_MODIFICATIONS_JSM_REQUEST_CREATE_RESOURCE]
            : [],
});

// Route for embeddable single portal page
const AsyncEmbedSinglePortalPage = withAsync({
    loader: lazyForPaint(
        () =>
            import(
                /* webpackChunkName: "async-embed-single-portal-page" */ '../../view/help-center/help-center-content/embed-single-portal'
            )
    ),
});

export const embedSinglePortalRoute: Route = {
    name: 'embed-single-portal',
    path: `/embeds/portal/:portalId`,
    exact: true,
    component: withRouter(withEmbeddedPage(AsyncEmbedSinglePortalPage, ScreenName.EMBED_SINGLE_PORTAL)),
};

// Route for embeddable knowledge base article page
const AsyncEmbedKBArticlePage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-embed-kb-article-page" */ '../../view/article/embed-kb-article')
    ),
});

export const embedKBArticleRoute = {
    name: 'embed-kb-article',
    path: `/embeds/portal/:portalId/:topic?/:categoryId?/:article/:articleId`,
    exact: true,
    component: withRouter(withEmbeddedPage(AsyncEmbedKBArticlePage, ScreenName.EMBED_KB_ARTICLE)),
};

// Route for embeddable featured portals page
const AsyncEmbedFeaturedPortalsPage = withAsync({
    loader: lazyForPaint(
        () =>
            import(
                /* webpackChunkName: "async-embed-featured-portals-page" */ '../../view/help-center/embed-featured-portals'
            )
    ),
});

export const embedFeaturedPortalsRoute = {
    name: 'embed-featured-portals',
    path: `/embeds/portals`,
    exact: true,
    component: withRouter(withEmbeddedPage(AsyncEmbedFeaturedPortalsPage, ScreenName.EMBED_FEATURED_PORTALS)),
};
// Route for embedded login success page
const AsyncEmbedLoginSuccessPage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-embed-login-success-page" */ '../../view/login/embed-login-success')
    ),
});

export const embedLoginSuccessRoute = {
    name: 'embed-login-success',
    path: `/embeds/login-success`,
    exact: true,
    component: withRouter(withEmbeddedPage(AsyncEmbedLoginSuccessPage, ScreenName.EMBED_LOGIN_SUCCESS)),
};

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const EmbedComponentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    border: ${grid.multiple(1 / 8).px} solid ${token('color.border.bold', colors.N30)};
    border-radius: ${grid.multiple(3 / 8).px};
    box-sizing: border-box;
`;
